import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'
import {
  AssignedUser,
  AssignMemberRoleForMeetingAdminRequest,
  BaseResponse,
  BatchRolesResponse,
  Role,
  UnassignedUser,
} from '../../generated'
import {
  useClubAdminAssignMemberRoleMutation,
  useClubAdminBatchAssignRolesMutation,
  useClubAdminBatchCancelRolesMutation,
} from '../../features/SpeechAgendaApiSlice.ts'
import {
  formatAdminAssignRoleResponse,
  parseErrorResponse,
} from '../../utils/FormatterUtils.ts'
import {
  GUEST_ROLE_ID,
  SPEAKER_ROLE_ID,
  SpeechDetails,
} from '../../types/Types.ts'
import SpeechDetailsDialog from '../modals/SpeechDetailsDialog.tsx'
import ConfirmActionDialog from '../modals/ConfirmActionDialog.tsx'
import { PathwayGroup } from '../../app/hooks/UsePathways.ts'

interface AdminRoleAssignmentProps {
  clubId: string
  meetingTimestampUtc: string
  roles: Role[]
  assignedUsers: AssignedUser[]
  unassignedUsers: UnassignedUser[]
  groups: PathwayGroup[]
  onResult: (success: boolean, title: string, description: string) => void
}

const roleKey = (role: Role) =>
  `${role.id}${role.linkedRoleId ? `-${role.linkedRoleId}` : ''}`

interface AutoAssigned {
  created: { userId: number; roleId: number; linkedRoleId?: number }[]
  expected: Set<string>
}

const pairKey = (pair: {
  userId: number
  roleId: number
  linkedRoleId?: number
}) => `${pair.userId}-${pair.roleId}-${pair.linkedRoleId ?? ''}`

const assignmentSignature = (assignedUsers: AssignedUser[]) =>
  new Set(assignedUsers.map(pairKey))

export default function AdminRoleAssignment({
  clubId,
  meetingTimestampUtc,
  roles,
  assignedUsers,
  unassignedUsers,
  groups,
  onResult,
}: AdminRoleAssignmentProps) {
  const openRoles = roles.filter((role) => role.isAvailable)

  const members = useMemo(() => {
    const byId = new Map<number, string>()
    unassignedUsers.forEach((member) => byId.set(member.id, member.name))
    assignedUsers.forEach((member) => {
      if (!byId.has(member.userId)) {
        byId.set(member.userId, member.userName)
      }
    })
    return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name)
    )
  }, [assignedUsers, unassignedUsers])

  const candidatesForRole = (
    role: Role,
    currentAssigned: AssignedUser[] = assignedUsers
  ) => {
    const linkedSpeakerUserId = role.linkedRoleId
      ? currentAssigned.find(
          (assigned) => assigned.meetingUserEntityId === role.linkedRoleId
        )?.userId
      : undefined
    return members.filter(
      (member) =>
        member.id !== linkedSpeakerUserId &&
        !currentAssigned.some(
          (assigned) =>
            assigned.userId === member.id && assigned.roleId === role.id
        )
    )
  }

  const isAutoAssignable = (role: Role, currentAssigned: AssignedUser[]) =>
    role.id !== SPEAKER_ROLE_ID &&
    role.id !== GUEST_ROLE_ID &&
    (!role.linkedRoleId ||
      currentAssigned.some(
        (assigned) => assigned.meetingUserEntityId === role.linkedRoleId
      ))

  const autoAssignableRoles = openRoles.filter((role) =>
    isAutoAssignable(role, assignedUsers)
  )

  const computeAssignments = (
    rolesToFill: Role[],
    currentAssigned: AssignedUser[]
  ) => {
    const guestUserIds = new Set(
      currentAssigned
        .filter((assigned) => assigned.roleId === GUEST_ROLE_ID)
        .map((assigned) => assigned.userId)
    )
    const eligibleFor = (role: Role) =>
      candidatesForRole(role, currentAssigned).filter(
        (member) => !guestUserIds.has(member.id)
      )
    const usedUserIds = new Set<number>()
    const items: AssignMemberRoleForMeetingAdminRequest[] = []
    const unfilledRoleNames: string[] = []
    const ordered = [...rolesToFill].sort(
      (a, b) => eligibleFor(a).length - eligibleFor(b).length
    )
    ordered.forEach((role) => {
      const candidates = eligibleFor(role).filter(
        (member) => !usedUserIds.has(member.id)
      )
      if (candidates.length === 0) {
        unfilledRoleNames.push(role.name)
        return
      }
      const pick = candidates[Math.floor(Math.random() * candidates.length)]
      usedUserIds.add(pick.id)
      items.push({
        userId: pick.id,
        roleId: role.id,
        linkedRoleId: role.linkedRoleId,
      })
    })
    return { items, unfilledRoleNames }
  }

  const [selectedUserByRole, setSelectedUserByRole] = useState<
    Record<string, string>
  >({})
  const [pendingSpeaker, setPendingSpeaker] = useState<{
    userId: number
    role: Role
  } | null>(null)
  const [autoAssigned, setAutoAssigned] = useState<AutoAssigned | null>(null)
  const [confirmClearOpen, setConfirmClearOpen] = useState(false)
  const [confirmAutoAssignOpen, setConfirmAutoAssignOpen] = useState(false)
  const [confirmRerollOpen, setConfirmRerollOpen] = useState(false)

  const [assignMemberRole, { isLoading }] =
    useClubAdminAssignMemberRoleMutation()
  const [batchAssignRoles, { isLoading: isBatchAssigning }] =
    useClubAdminBatchAssignRolesMutation()
  const [batchCancelRoles, { isLoading: isBatchCancelling }] =
    useClubAdminBatchCancelRolesMutation()
  const isBatchBusy = isBatchAssigning || isBatchCancelling

  useEffect(() => {
    setAutoAssigned(null)
  }, [clubId, meetingTimestampUtc])

  const currentSignature = useMemo(
    () => assignmentSignature(assignedUsers),
    [assignedUsers]
  )

  const canReroll =
    !!autoAssigned &&
    autoAssigned.created.length > 0 &&
    autoAssigned.expected.size === currentSignature.size &&
    [...autoAssigned.expected].every((pair) => currentSignature.has(pair))

  const handleBatchError = (error: unknown, fallback: string) => {
    const data = (error as { data?: BaseResponse }).data
    if (data?.status) {
      const { title, description } = parseErrorResponse(data)
      onResult(false, title, description)
    } else {
      onResult(false, 'Something went wrong', fallback)
    }
  }

  const restoreTracked = async (
    tracked: { userId: number; roleId: number; linkedRoleId?: number }[]
  ) => {
    try {
      await batchAssignRoles({
        clubId,
        meetingTimestampUtc,
        body: {
          items: tracked.map((pair) => ({
            userId: pair.userId,
            roleId: pair.roleId,
            linkedRoleId: pair.linkedRoleId,
          })),
        },
      }).unwrap()
    } catch {
      // Best-effort restore; nothing else to do if it also fails.
    }
  }

  const buildAssignSummary = (
    response: BatchRolesResponse,
    unfilledRoleNames: string[]
  ) => {
    const roleNameById = new Map(roles.map((role) => [role.id, role.name]))
    const failedRoleNames = response.results
      .filter((result) => result.status === 'FAILED')
      .map((result) => roleNameById.get(result.roleId) ?? `Role ${result.roleId}`)
    const { succeeded } = response.summary
    const description = [
      `Assigned ${succeeded} role${succeeded === 1 ? '' : 's'}.`,
      ...(unfilledRoleNames.length + failedRoleNames.length > 0
        ? [`Left unfilled: ${[...unfilledRoleNames, ...failedRoleNames].join(', ')}.`]
        : []),
    ].join(' ')
    return description
  }

  const trackSucceeded = (
    response: BatchRolesResponse,
    items: AssignMemberRoleForMeetingAdminRequest[]
  ) =>
    response.results
      .filter((result) => result.status === 'SUCCEEDED')
      .map((result) => ({
        userId: result.userId,
        roleId: result.roleId,
        linkedRoleId: items.find(
          (item) =>
            item.userId === result.userId && item.roleId === result.roleId
        )?.linkedRoleId,
      }))

  const handleAutoAssign = async () => {
    setConfirmAutoAssignOpen(false)
    const { items, unfilledRoleNames } = computeAssignments(
      autoAssignableRoles,
      assignedUsers
    )
    if (items.length === 0) {
      onResult(
        false,
        'Nothing to auto-assign',
        'There are no eligible members for the open roles.'
      )
      return
    }
    try {
      const response = await batchAssignRoles({
        clubId,
        meetingTimestampUtc,
        body: { items },
      }).unwrap()
      const description = buildAssignSummary(response, unfilledRoleNames)
      if (response.summary.succeeded === 0) {
        setAutoAssigned(null)
        onResult(false, 'Auto-assign failed', description)
        return
      }
      const created = trackSucceeded(response, items)
      const expected = assignmentSignature(assignedUsers)
      created.forEach((pair) => expected.add(pairKey(pair)))
      setAutoAssigned({ created, expected })
      onResult(true, 'Roles auto-assigned', description)
    } catch (error) {
      setAutoAssigned(null)
      handleBatchError(error, 'We could not auto-assign roles. Please try again.')
    }
  }

  const handleReroll = async () => {
    setConfirmRerollOpen(false)
    if (!autoAssigned) {
      return
    }
    const tracked = autoAssigned.created
    const simulatedAssigned = assignedUsers.filter(
      (assigned) =>
        !tracked.some(
          (pair) =>
            pair.userId === assigned.userId && pair.roleId === assigned.roleId
        )
    )
    const rolesToFill = tracked
      .map((pair) =>
        roles.find(
          (role) =>
            roleKey(role) ===
            `${pair.roleId}${pair.linkedRoleId ? `-${pair.linkedRoleId}` : ''}`
        )
      )
      .filter((role): role is Role => !!role)
    const { items, unfilledRoleNames } = computeAssignments(
      rolesToFill,
      simulatedAssigned
    )
    let cancelFailed = 0
    try {
      const cancelResponse = await batchCancelRoles({
        clubId,
        meetingTimestampUtc,
        body: {
          clearAll: false,
          items: tracked.map((pair) => ({
            userId: pair.userId,
            roleId: pair.roleId,
          })),
        },
      }).unwrap()
      cancelFailed = cancelResponse.summary.failed
    } catch (error) {
      handleBatchError(error, 'We could not re-roll the roles. Please try again.')
      return
    }
    if (items.length === 0) {
      setAutoAssigned(null)
      onResult(
        cancelFailed === 0,
        cancelFailed === 0 ? 'Roles cleared' : 'Re-roll incomplete',
        cancelFailed === 0
          ? 'The previous auto-assignment was removed, but no eligible members were available to re-roll.'
          : `${cancelFailed} previous assignment${
              cancelFailed === 1 ? '' : 's'
            } could not be removed, and no eligible members were available to re-roll.`
      )
      return
    }
    try {
      const response = await batchAssignRoles({
        clubId,
        meetingTimestampUtc,
        body: { items },
      }).unwrap()
      const description = buildAssignSummary(response, unfilledRoleNames)
      if (response.summary.succeeded === 0) {
        await restoreTracked(tracked)
        setAutoAssigned(null)
        onResult(
          false,
          'Re-roll failed',
          `${description} Your previous assignments were restored where possible.`
        )
        return
      }
      const created = trackSucceeded(response, items)
      const expected = assignmentSignature(simulatedAssigned)
      created.forEach((pair) => expected.add(pairKey(pair)))
      setAutoAssigned({ created, expected })
      onResult(true, 'Roles re-rolled', description)
    } catch (error) {
      await restoreTracked(tracked)
      setAutoAssigned(null)
      handleBatchError(
        error,
        'We could not re-roll the roles. Your previous assignments were restored where possible.'
      )
    }
  }

  const handleClear = async () => {
    setConfirmClearOpen(false)
    try {
      const response = await batchCancelRoles({
        clubId,
        meetingTimestampUtc,
        body: { clearAll: true, items: [] },
      }).unwrap()
      setAutoAssigned(null)
      const { succeeded, failed } = response.summary
      if (failed > 0) {
        onResult(
          succeeded > 0,
          succeeded > 0 ? 'Assignments partially cleared' : 'Clear failed',
          `Removed ${succeeded} assignment${
            succeeded === 1 ? '' : 's'
          }; ${failed} could not be removed.`
        )
        return
      }
      onResult(
        true,
        'Assignments cleared',
        `Removed ${succeeded} assignment${
          succeeded === 1 ? '' : 's'
        } for this meeting.`
      )
    } catch (error) {
      handleBatchError(error, 'We could not clear the assignments. Please try again.')
    }
  }

  const submitAssignment = async (
    userId: number,
    role: Role,
    speechDetails?: SpeechDetails
  ) => {
    const speechTitle = speechDetails?.speechTitle?.trim()
    try {
      const response = await assignMemberRole({
        clubId,
        meetingTimestampUtc,
        body: {
          userId,
          roleId: role.id,
          linkedRoleId: role.linkedRoleId,
          speechTitle: speechTitle || undefined,
          speechDescription: speechDetails?.speechDescription,
          pathway: speechDetails?.pathway,
          pathwayLevel: speechDetails?.pathwayLevel,
        },
      }).unwrap()
      setSelectedUserByRole((prev) => {
        const next = { ...prev }
        delete next[roleKey(role)]
        return next
      })
      setPendingSpeaker(null)
      const memberName =
        members.find((member) => member.id === userId)?.name ?? 'The member'
      const { title, description } = formatAdminAssignRoleResponse(
        response,
        memberName
      )
      onResult(true, title, description)
    } catch (error) {
      setPendingSpeaker(null)
      const data = (error as { data?: BaseResponse }).data
      if (data?.status) {
        const { title, description } = parseErrorResponse(data)
        onResult(false, title, description)
      } else {
        onResult(
          false,
          'Something went wrong',
          'We could not assign this role. Please try again.'
        )
      }
    }
  }

  const handleAssign = (role: Role) => {
    const userId = Number(selectedUserByRole[roleKey(role)])
    if (!userId) {
      return
    }
    if (role.id === SPEAKER_ROLE_ID) {
      setPendingSpeaker({ userId, role })
      return
    }
    void submitAssignment(userId, role)
  }

  const showAutoAssign = autoAssignableRoles.length > 0 && members.length > 0
  const showActions = showAutoAssign || canReroll || assignedUsers.length > 0

  return (
    <div className="py-6">
      <dt className="text-base font-semibold leading-6 text-gray-900">
        Assign roles
      </dt>
      <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
        Assign the open roles below to your club members for the upcoming
        meeting
      </dd>
      {showActions && (
        <div className="mt-4 flex flex-wrap gap-3">
          {showAutoAssign && (
            <button
              type="button"
              disabled={isBatchBusy || isLoading}
              onClick={() => setConfirmAutoAssignOpen(true)}
              className="inline-flex justify-center items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50">
              Auto-assign roles
            </button>
          )}
          {canReroll && (
            <button
              type="button"
              disabled={isBatchBusy || isLoading}
              onClick={() => setConfirmRerollOpen(true)}
              className="inline-flex justify-center items-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
              Re-roll
            </button>
          )}
          {assignedUsers.length > 0 && (
            <button
              type="button"
              disabled={isBatchBusy || isLoading}
              onClick={() => setConfirmClearOpen(true)}
              className="inline-flex justify-center items-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
              Clear
            </button>
          )}
        </div>
      )}
      {openRoles.length === 0 ? (
        <p className="mt-6 text-sm text-gray-500">
          All roles for this meeting are filled.
        </p>
      ) : members.length === 0 ? (
        <p className="mt-6 text-sm text-gray-500">
          No club members yet.{' '}
          <Link
            to={`/club/${clubId}/admin`}
            className="font-medium text-indigo-600 hover:text-indigo-500">
            Add a member from the Club Admin panel.
          </Link>
        </p>
      ) : (
        <div className="mt-6 space-y-3 max-w-xl">
          {openRoles.map((role) => (
            <div
              key={roleKey(role)}
              className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
              <label
                htmlFor={`assign-${roleKey(role)}`}
                className="block text-sm font-semibold text-gray-900 sm:w-44 sm:flex-shrink-0 sm:font-normal">
                {role.name}
              </label>
              <select
                id={`assign-${roleKey(role)}`}
                value={selectedUserByRole[roleKey(role)] ?? ''}
                onChange={(event) =>
                  setSelectedUserByRole((prev) => ({
                    ...prev,
                    [roleKey(role)]: event.target.value,
                  }))
                }
                className="block rounded-md border-0 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:mr-7 sm:flex-[0.77_1_0%]">
                <option value="">Select a member</option>
                {candidatesForRole(role).map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={
                  !selectedUserByRole[roleKey(role)] || isLoading || isBatchBusy
                }
                onClick={() => handleAssign(role)}
                className="inline-flex justify-center items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-shrink-0">
                Assign
              </button>
            </div>
          ))}
        </div>
      )}
      <SpeechDetailsDialog
        isOpen={!!pendingSpeaker}
        onClose={() => setPendingSpeaker(null)}
        onSubmit={(speechDetails) => {
          if (pendingSpeaker) {
            void submitAssignment(
              pendingSpeaker.userId,
              pendingSpeaker.role,
              speechDetails
            )
          }
        }}
        groups={groups}
        isSubmitting={isLoading}
        title="Speech details (optional)"
        description="Optionally add speech details for this speaker. You can assign the role without them."
        submitLabel="Assign speaker"
      />
      <ConfirmActionDialog
        isOpen={confirmAutoAssignOpen}
        variant="default"
        title="Auto-assign open roles?"
        description="Randomly assign eligible members to the open roles for this meeting?"
        confirmText="Auto-assign"
        cancelText="Cancel"
        onConfirm={() => void handleAutoAssign()}
        onCancel={() => setConfirmAutoAssignOpen(false)}
      />
      <ConfirmActionDialog
        isOpen={confirmRerollOpen}
        variant="default"
        title="Re-roll assignments?"
        description="Replace the auto-assigned roles with a fresh random selection?"
        confirmText="Re-roll"
        cancelText="Cancel"
        onConfirm={() => void handleReroll()}
        onCancel={() => setConfirmRerollOpen(false)}
      />
      <ConfirmActionDialog
        isOpen={confirmClearOpen}
        title="Clear all assignments?"
        description="Remove all assignments for this meeting? This cannot be undone."
        confirmText="Clear all"
        cancelText="Keep"
        onConfirm={() => void handleClear()}
        onCancel={() => setConfirmClearOpen(false)}
      />
    </div>
  )
}
